import React from "react";
import cx from "classnames";
import { useTranslation, Link } from "gatsby-plugin-react-i18next";
import SecondaryButton from "../Button/SecondaryButton";
import { GatsbyImage } from "gatsby-plugin-image";
import { fullWidth } from "components/Design/ArtDirectedImage";
import Icon from "components/CommonIcon/Icon";
import IconLinkButton from "components/Design/Button/IconLinkButton";
import ConditionalLink from "components/ConditionalLink";

const CardRoom = ({ room, useLinkButton, showDetails, ...rest }) => {
  const { t } = useTranslation("homepage");
  const { title, image, icons, ctaDetail, name } = room;

  return (
    <div className="lg:max-w-870" {...rest}>
      <div className="h-188 md:h-488">
        <Link to={room.ctaDetail}>
          <GatsbyImage
            image={fullWidth(image)}
            className="h-full rounded-sm overflow-hidden w-full"
            imgClassName="rounded-sm select-none"
            alt={title}
            loading="lazy"
          />
        </Link>
      </div>
      <div className={cx({ "lg:hidden": !showDetails })}>
        <Link to={room.ctaDetail}>
          <div className="font-semibold text-h4 lg:text-h3 text-gray-text pt-12 lg:pt-20">
            {name}
          </div>
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="grid grid-cols-7 lg:grid-cols-none lg:grid-flow-col lg:auto-cols-max lg:gap-x-20 font-mulish lg:gap-y-0 gap-y-8 mt-8 lg:mt-0">
            {icons &&
              icons.map((icon, index) => {
                return (
                  <div
                    key={index}
                    className={cx(
                      "flex lg:ml-0 justify-start align-middle items-center",
                      {
                        "col-span-4": index % 2,
                        "col-span-3": !(index % 2),
                      }
                    )}>
                    {icon.icon}
                    <span className="items-center pl-5 text-12-22 text-gray-text tracking-2">
                      {icon.name}
                    </span>
                  </div>
                );
              })}
          </div>
          <div className="text-left lg:text-right">
            {ctaDetail && (
              <Link to={ctaDetail}>
                <div className="lg:block hidden">
                  {useLinkButton ? (
                    <div className="flex align-middle items-center justify-items-end justify-end">
                      <span className="flex items-center font-mulish text-12-22 uppercase text-gold tracking-16 font-semibold">
                        {t("See Details")}
                      </span>
                      <span className="ml-10">
                        <Icon name="right-arrow" fontSize="text-16" />
                      </span>
                    </div>
                  ) : (
                    <SecondaryButton label={t("See Details")} />
                  )}
                </div>

                <div className="flex align-middle items-center justify-start lg:hidden mt-14">
                  <span className="flex items-center font-mulish text-12-22 uppercase text-gold tracking-16 font-semibold">
                    {t("See Details")}
                  </span>
                  <span className="ml-10">
                    <Icon name="right-arrow" fontSize="text-16" />
                  </span>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CardRoomListMobile = ({ rooms }) => {
  return (
    <div className="grid grid-cols-1 gap-y-24">
      {rooms &&
        rooms.map((room, idx) => {
          return (
            <div
              key={idx}
              className="border-b border-gold border-opacity-50 pb-20">
              {room}
            </div>
          );
        })}
    </div>
  );
};

const CardHighlightRoom = ({
  name,
  title,
  subtitle,
  icons,
  policyDetail,
  description,
  ctaDetail,
  showCtaAsLink,
  type,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cx("w-full flex flex-col justify-between", {
        "h-full": type === "gray",
      })}>
      <div>
        {subtitle && (
          <span
            className={cx("font-mulish text-gold font-bold block", {
              uppercase: type === "gray",
              "text-14-24 font-normal": type === "white",
              "text-12 mb-10 lg:mb-12 tracking-16": type !== "white",
            })}>
            {subtitle}
          </span>
        )}

        <Link to={ctaDetail}>
          <h1 className="font-semibold text-26-36">{title}</h1>
        </Link>
        <div
          className={cx("grid grid-cols-1", {
            "my-10 md:my-12": type !== "white",
          })}>
          <div className="grid grid-cols-2">
            {icons.map((icon) => {
              return (
                <div
                  key={icon.name}
                  className="flex lg:ml-0 justify-start align-middle items-center">
                  {icon.icon}
                  <span className="font-mulish items-center pl-5 text-12-22 text-gray-text tracking-2">
                    {icon.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {policyDetail && (
          <div
            className={cx("font-mulish text-14-24 mt-8", {
              "text-gray-text mb-30": type === "gray",
              "text-white mb-16": type === "white",
            })}>
            {policyDetail}
          </div>
        )}
        <p className="font-mulish text-14-24">
          {description.length <= 360
            ? description
            : `${description.substring(0, 382)}...`}
        </p>
        {ctaDetail && showCtaAsLink && (
          <div className="inline-block mt-8">
            <IconLinkButton
              href={ctaDetail}
              btnText={t("See Detail")}
              smaller
            />
          </div>
        )}
      </div>
      {ctaDetail && !showCtaAsLink && (
        <ConditionalLink
          link={ctaDetail}
          className="inline-block mt-12 lg:mt-30 text-right lg:text-left"
          sameTab>
          <SecondaryButton label={t("See Detail")} />
        </ConditionalLink>
      )}
    </div>
  );
};

CardHighlightRoom.defaultProps = {
  title: "",
  subtitle: "",
  description: "",
  icons: [],
  type: "gray",
};

export { CardRoom, CardRoomListMobile, CardHighlightRoom };
